(function initializeGoGreenMaterialsCalculator(global) {
  if (global.GoGreenMaterialsCalculator) return;

  const registry = new Map();

  function parseNumber(value) {
    const number = Number.parseFloat(String(value ?? '').replace(/,/g, ''));
    return Number.isFinite(number) ? number : 0;
  }

  function positiveNumber(value) {
    return Math.max(0, parseNumber(value));
  }

  function convertWeightToLbs(weight, unit) {
    const value = positiveNumber(weight);
    if (!value) return 0;

    switch (String(unit || '').toLowerCase()) {
      case 'kg':
        return value * 2.2046226218;
      case 'g':
        return value * 0.0022046226;
      case 'oz':
        return value / 16;
      default:
        return value;
    }
  }

  function calculateSeams({ length, width, rollWidth }) {
    const projectLength = positiveNumber(length);
    const projectWidth = positiveNumber(width);
    const turfRollWidth = positiveNumber(rollWidth);
    const squareFeet = projectLength * projectWidth;

    if (!projectLength || !projectWidth || !turfRollWidth) {
      return {
        squareFeet,
        estimatedSeamLinearFeet: 0,
        missingRollWidth: projectLength > 0 && projectWidth > 0 && !turfRollWidth,
      };
    }

    const seamLengthA = Math.max(Math.ceil(projectWidth / turfRollWidth) - 1, 0) * projectLength;
    const seamLengthB = Math.max(Math.ceil(projectLength / turfRollWidth) - 1, 0) * projectWidth;

    return {
      squareFeet,
      estimatedSeamLinearFeet: Math.ceil((Math.min(seamLengthA, seamLengthB) + Math.max(seamLengthA, seamLengthB)) / 2),
      missingRollWidth: false,
    };
  }

  function calculateTurfLineArea(line) {
    if (line?.properties?._turf_cut !== 'true') return 0;

    const linearFeet = positiveNumber(line.properties._linear_feet);
    const rollWidth = positiveNumber(line.properties._roll_width_ft);
    const quantity = positiveNumber(line.quantity);
    if (!linearFeet || !rollWidth || !quantity) return 0;

    return linearFeet * rollWidth * quantity;
  }

  function normalizeMaterial(material, quantity, warning = '') {
    const normalizedQuantity = Math.max(0, Math.ceil(positiveNumber(quantity)));
    const price = Math.max(0, Math.round(positiveNumber(material?.price)));

    return {
      kind: material?.kind || '',
      productId: material?.productId ? String(material.productId) : '',
      variantId: material?.variantId ? String(material.variantId) : '',
      sku: material?.sku || '',
      label: material?.label || 'Recommended material',
      quantity: normalizedQuantity,
      price,
      linePrice: price * normalizedQuantity,
      available: material?.available !== false,
      warning,
    };
  }

  function registryKeys(config) {
    return [
      config?.productId ? `product:${config.productId}` : '',
      config?.variantId ? `variant:${config.variantId}` : '',
    ].filter(Boolean);
  }

  function registerProduct(config) {
    if (!config || typeof config !== 'object') return null;
    for (const key of registryKeys(config)) registry.set(key, config);
    return config;
  }

  function getProductConfig({ productId, variantId, config } = {}) {
    if (config) return config;
    if (variantId && registry.has(`variant:${variantId}`)) return registry.get(`variant:${variantId}`);
    if (productId && registry.has(`product:${productId}`)) return registry.get(`product:${productId}`);
    return null;
  }

  function calculate(input = {}) {
    const config = getProductConfig(input);
    const properties = input.properties || {};
    const length = positiveNumber(input.length || properties._linear_feet);
    const width = positiveNumber(input.width || properties._roll_width_ft);
    const squareFeet = positiveNumber(input.squareFeet) || length * width;
    const seamCalculation = calculateSeams({
      length,
      width,
      rollWidth: input.rollWidth || config?.rollWidthFeet || properties._roll_width_ft,
    });
    const estimatedSeamLinearFeet = positiveNumber(input.estimatedSeamLinearFeet) || seamCalculation.estimatedSeamLinearFeet;
    const recommendations = [];
    const warnings = [];

    if (!config) {
      warnings.push('No installation-material rules are registered for this product or variant.');
      return { squareFeet, estimatedSeamLinearFeet, recommendations, warnings };
    }

    for (const material of config.materials || []) {
      let quantity = 0;
      let warning = '';

      if (material.kind === 'infill') {
        if (!config.requiresInfill) {
          warning = 'This turf does not require infill.';
        } else {
          const lbsPerSqft = positiveNumber(config.infillLbsPerSqft);
          const bagWeightLbs = convertWeightToLbs(material.weight, material.weightUnit);
          if (!lbsPerSqft) warning = 'Turf requires infill, but lbs per sqft is missing.';
          else if (!bagWeightLbs) warning = 'Turf requires infill, but infill bag weight is missing.';
          else quantity = Math.ceil((squareFeet * lbsPerSqft) / bagWeightLbs);
        }
      } else if (material.kind === 'glue' || material.kind === 'seam_tape') {
        const coverage = positiveNumber(material.coverageLinearFeet);
        if (seamCalculation.missingRollWidth) warning = 'Cannot calculate seams because turf roll width is missing.';
        else if (estimatedSeamLinearFeet > 0 && !coverage) warning = `Cannot calculate ${material.kind === 'glue' ? 'glue' : 'seam tape'} quantity because linear-foot coverage is missing.`;
        else if (estimatedSeamLinearFeet > 0) quantity = Math.ceil(estimatedSeamLinearFeet / coverage);
      }

      if (warning) warnings.push(warning);
      recommendations.push(normalizeMaterial(material, quantity, warning));
    }

    return { squareFeet, estimatedSeamLinearFeet, recommendations, warnings };
  }

  function calculateCart({ lines = [], existingVariantQuantities = {} } = {}) {
    if (Array.isArray(existingVariantQuantities)) {
      existingVariantQuantities = existingVariantQuantities.reduce((quantities, item) => {
        const variantId = String(item?.variantId || '');
        if (!variantId) return quantities;
        quantities[variantId] = positiveNumber(quantities[variantId]) + positiveNumber(item.quantity);
        return quantities;
      }, {});
    }

    const groups = new Map();
    let squareFeet = 0;

    for (const line of lines) {
      if (line?.properties?._turf_cut !== 'true') continue;
      const length = positiveNumber(line.properties._linear_feet);
      const quantity = positiveNumber(line.quantity);
      const lineSquareFeet = calculateTurfLineArea(line);
      if (!lineSquareFeet || !line.config) continue;

      squareFeet += lineSquareFeet;

      for (const material of line.config.materials || []) {
        if (!material?.variantId) continue;
        const key = String(material.variantId);
        const group = groups.get(key) || {
          material,
          requiredLbs: 0,
          totalRollLength: 0,
          maxRollLength: 0,
        };

        if (material.kind === 'infill' && line.config.requiresInfill) {
          group.requiredLbs += lineSquareFeet * positiveNumber(line.config.infillLbsPerSqft);
        } else if (material.kind === 'glue' || material.kind === 'seam_tape') {
          group.totalRollLength += length * quantity;
          group.maxRollLength = Math.max(group.maxRollLength, length);
        }

        groups.set(key, group);
      }
    }

    const recommendations = [];
    for (const [variantId, group] of groups) {
      let requiredQuantity = 0;
      let warning = '';

      if (group.material.kind === 'infill') {
        const bagWeightLbs = convertWeightToLbs(group.material.weight, group.material.weightUnit);
        if (!bagWeightLbs && group.requiredLbs > 0) warning = 'Infill bag weight is missing.';
        else if (bagWeightLbs) requiredQuantity = Math.ceil(group.requiredLbs / bagWeightLbs);
      } else {
        const seamLinearFeet = Math.max(group.totalRollLength - group.maxRollLength, 0);
        const coverage = positiveNumber(group.material.coverageLinearFeet);
        if (!coverage && seamLinearFeet > 0) warning = 'Linear-foot coverage is missing.';
        else if (coverage) requiredQuantity = Math.ceil(seamLinearFeet / coverage);
      }

      const existingQuantity = positiveNumber(existingVariantQuantities[variantId]);
      const missingQuantity = Math.max(requiredQuantity - existingQuantity, 0);
      recommendations.push({
        ...normalizeMaterial(group.material, missingQuantity, warning),
        requiredQuantity,
        existingQuantity,
      });
    }

    return {
      squareFeet,
      recommendations: recommendations.filter((item) => item.quantity > 0),
      warnings: recommendations.map((item) => item.warning).filter(Boolean),
    };
  }

  function registerFromDocument(root) {
    if (!root?.querySelectorAll) return;
    for (const element of root.querySelectorAll('[data-gg-materials-product-config]')) {
      if (element.dataset.registered === 'true') continue;
      try {
        registerProduct(JSON.parse(element.textContent || '{}'));
        element.dataset.registered = 'true';
      } catch (error) {
        console.error('[Go Green materials] Invalid product configuration.', error);
      }
    }
  }

  const api = {
    version: '1.0.0',
    calculate,
    calculateCart,
    calculateSeams,
    calculateTurfLineArea,
    convertWeightToLbs,
    registerProduct,
    registerFromDocument,
  };

  global.GoGreenMaterialsCalculator = api;

  if (global.document) {
    const initialize = () => registerFromDocument(global.document);
    if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
    global.document.addEventListener('shopify:section:load', (event) => registerFromDocument(event.target));
    global.document.dispatchEvent(new CustomEvent('gg:materials-calculator:ready', { detail: api }));
  }
})(typeof window !== 'undefined' ? window : globalThis);
